import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { architectures } from "../../data/architectures";

const Trigger = styled.button`
  display: inline-flex;
  align-items: center;
  padding: 9px 14px;
  border: 1px solid rgba(255, 255, 255, 0.35);
  border-radius: 0;
  background: transparent;
  color: ${({ theme }) => theme.biome.projects.ink};
  font-family: ${({ theme }) => theme.fonts.ui};
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  cursor: pointer;

  &:hover, &:focus-visible {
    border-color: ${({ theme }) => theme.orange};
    color: ${({ theme }) => theme.orange};
  }
`;

const Dialog = styled.dialog`
  position: fixed;
  inset: 0;
  width: min(880px, calc(100vw - 32px));
  height: min(640px, calc(100dvh - 32px));
  max-width: none;
  max-height: none;
  box-sizing: border-box;
  margin: auto;
  padding: 0;
  border: 2px solid ${({ theme }) => theme.orange};

  &[open] { display: flex; flex-direction: column; }
  background: ${({ theme }) => theme.biome.projects.card};
  color: ${({ theme }) => theme.biome.projects.ink};

  &::backdrop { background: rgba(0, 0, 0, 0.82); }

  header {
    flex: 0 0 auto;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 16px;
    padding: 20px 24px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.2);
    background: ${({ theme }) => theme.biome.projects.card};
  }

  h2 { margin: 0; font-size: clamp(22px, 4vw, 32px); line-height: 1.15; }
  header button {
    flex: 0 0 auto;
    padding: 7px 10px;
    border: 1px solid currentColor;
    background: transparent;
    color: ${({ theme }) => theme.biome.projects.ink};
    cursor: pointer;
  }
  button:focus-visible, main:focus-visible {
    outline: 2px solid ${({ theme }) => theme.orange};
    outline-offset: 3px;
  }

  main {
    flex: 1 1 auto;
    min-height: 0;
    overflow: hidden;
    padding: 16px;
    background: #ffffff;
    color: #222222;
  }
  main > div { height: 100%; }
  main svg {
    display: block;
    width: 100%;
    height: 100%;
  }

  @media (max-width: 640px) {
    header { padding: 18px 16px; flex-direction: column; }
    header button { align-self: flex-end; }
    main { padding: 8px; }
  }
`;

const ArchitectureDialog = ({ project }) => {
  const dialog = useRef(null);
  const [open, setOpen] = useState(false);
  const [svg, setSvg] = useState("");
  const [error, setError] = useState(false);
  const source = architectures[project.id];

  useEffect(() => {
    if (!open || !source || svg) return;
    let active = true;

    import("mermaid")
      .then(({ default: mermaid }) => {
        mermaid.initialize({ startOnLoad: false, securityLevel: "strict", theme: "default", flowchart: { useMaxWidth: false } });
        return mermaid.render(`architecture-svg-${project.id}`, source);
      })
      .then(({ svg: rendered }) => { if (active) setSvg(rendered); })
      .catch(() => { if (active) setError(true); });

    return () => { active = false; };
  }, [open, source, svg, project.id]);

  if (!source) return null;

  return (
    <>
      <Trigger type="button" onClick={() => { dialog.current.showModal(); setOpen(true); }}>
        Architecture
      </Trigger>
      <Dialog
        ref={dialog}
        aria-labelledby={`architecture-${project.id}`}
        onClose={() => setOpen(false)}
        onClick={(event) => { if (event.target === dialog.current) dialog.current.close(); }}
      >
        <header>
          <h2 id={`architecture-${project.id}`}>{project.title}</h2>
          <button type="button" onClick={() => dialog.current.close()} aria-label="Close architecture">Close</button>
        </header>
        <main tabIndex="0" aria-label={`${project.title} architecture diagram`}>
          {error ? "Diagram could not load." : svg ? (
            <div role="img" aria-label={`${project.title} architecture`} dangerouslySetInnerHTML={{ __html: svg }} />
          ) : "Rendering diagram…"}
        </main>
      </Dialog>
    </>
  );
};

export default ArchitectureDialog;
